// src/pages/auth/LoginPage.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography, Divider } from '@mui/material';
import { useAuth } from '../../../context/AuthContext';

export default function Login() {
  const [error, setError] = useState('');
  const [autoTried, setAutoTried] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { loginSso, loading /* , loginLegacy */ } = useAuth();
  const triedRef = useRef(false); // ensure we only auto-try once per mount

  // --- Helpers ---
  const isEdge = () => /Edg\//.test(window.navigator.userAgent);

  const isReturningFromAuth = () => {
    // If your IdP returns query params like ?code=&state= on redirect:
    const params = new URLSearchParams(location.search);
    return (
      params.has('code') ||
      params.has('state') ||
      params.has('session') ||
      params.has('ticket')
    );
  };

  const cameFromLogout = () => {
    const params = new URLSearchParams(location.search);
    return params.get('logged_out') === '1';
  };

  // Auto-execute SSO:
  // - Do NOT auto-run if we are returning from an auth redirect (to avoid loops)
  // - Prefer auto-run on Edge (good fit for WIA/Kerberos/Seamless SSO)
  // - Only try once
  useEffect(() => {
    const shouldAutoSso =
      !triedRef.current &&
      !isReturningFromAuth() &&
      !cameFromLogout() &&
      (isEdge() || localStorage.getItem('autoSSO') === '1');

    if (!shouldAutoSso) return;

    triedRef.current = true;

    (async () => {
      setError('');
      setAutoTried(true);
      const res = await loginSso(/* e.g. { prompt: 'none' } if supported */);

      if (res?.success) {
        navigate('/');
      } else if (res?.redirect) {
        // Popup was blocked; MSAL is doing a full-page redirect instead.
        setRedirecting(true);
        // No need to set an error; browser will navigate away.
      } else {
        setError(res?.error || 'Automatic SSO failed. You can try again below.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginSso, navigate]);

  const handleSso = async () => {
    setError('');
    setRedirecting(false);

    const res = await loginSso();

    if (res?.success) {
      navigate('/');
    } else if (res?.redirect) {
      // Popup blocked → loginRedirect in progress
      setRedirecting(true);
    } else {
      setError(res?.error || 'SSO failed');
    }
  };

  const buttonLabel = (() => {
    if (redirecting) return 'Redirecting to Microsoft sign-in…';
    if (loading) return autoTried ? 'Signing in… (SSO)' : 'Signing in…';
    return 'Sign in with Axillon';
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Typography variant="h4" mb={2}>
        Sign in
      </Typography>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      {redirecting && !error && (
        <Typography variant="body2" mb={2}>
          Redirecting you to Microsoft to complete sign-in…
        </Typography>
      )}

      <Divider sx={{ my: 3, width: '100%', maxWidth: 400 }} />

      {/* SSO button */}
      <Button
        variant="outlined"
        onClick={handleSso}
        fullWidth
        disabled={loading || redirecting}
        sx={{ maxWidth: 400 }}
      >
        {buttonLabel}
      </Button>

      {/* ===== LEGACY FORM (commented out) =====
      ----------------------------------------------------------------
      // If you ever need to re-enable legacy auth, uncomment below
      // and add email/password state back in.
      <Divider sx={{ my: 3, width: '100%', maxWidth: 400 }}>or</Divider>

      <form onSubmit={handleLegacy} style={{ width: '100%', maxWidth: 400 }}>
        <TextField
          label="Email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Password"
          type="password"
          variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <Button variant="contained" type="submit" fullWidth disabled={loading}>
          {loading ? 'Logging in…' : 'Login (Legacy)'}
        </Button>
        <Box mt={2}>
          <Link href="/password-reset" variant="body2">
            Forgot your password?
          </Link>
        </Box>
        <Box mt={1}>
          <Link href="/register" variant="body2">
            Don&apos;t have an account? Register here.
          </Link>
        </Box>
      </form>
      ----------------------------------------------------------------
      */}
    </Box>
  );
}
